/**
 * Maps ISO 18245 Merchant Category Codes (MCC) to human-readable categories. There are ~1000 MCCs;
 * translating every description to three languages is impractical, so we group them into a
 * granular-but-bounded set of categories whose labels live in i18n (`mcc_category_*` keys). The raw MCC is
 * still shown next to the label for precision (e.g. "Taxis · 4121").
 *
 * The `MCC_TO_CATEGORY` table is generated from the public ISO 18245 dataset (greggles/mcc-codes) and covers
 * the full standard code list. Brand ranges 3000-3299 (airlines) and 3300-3499 (car rental) map accordingly;
 * 3500-3999 (hotels) map to "lodging". Unknown codes fall back to `null` (the UI then shows only the raw
 * code).
 */
export type MccCategory =
  | "taxi_rideshare"
  | "public_transit"
  | "airlines"
  | "travel"
  | "car_rental"
  | "lodging"
  | "groceries"
  | "restaurants"
  | "fast_food"
  | "bars"
  | "liquor"
  | "fuel"
  | "automotive"
  | "apparel"
  | "shoes"
  | "jewelry"
  | "electronics"
  | "department_stores"
  | "discount_stores"
  | "furniture"
  | "home_improvement"
  | "books_stationery"
  | "sporting_goods"
  | "toys_hobby"
  | "gifts"
  | "florist"
  | "pets"
  | "cosmetics"
  | "music_media"
  | "general_retail"
  | "wholesale"
  | "technology"
  | "telecom"
  | "utilities"
  | "entertainment"
  | "gambling"
  | "medical"
  | "pharmacy"
  | "education"
  | "financial"
  | "insurance"
  | "real_estate"
  | "professional_services"
  | "personal_care"
  | "repair_services"
  | "cleaning_laundry"
  | "contractors"
  | "shipping"
  | "government"
  | "charity"
  | "membership_orgs"
  | "services_other";

/** I18n key (cards namespace) per category. */
export const MCC_CATEGORY_TKEY: Record<MccCategory, string> = {
  taxi_rideshare: "mcc_category_taxi_rideshare",
  public_transit: "mcc_category_public_transit",
  airlines: "mcc_category_airlines",
  travel: "mcc_category_travel",
  car_rental: "mcc_category_car_rental",
  lodging: "mcc_category_lodging",
  groceries: "mcc_category_groceries",
  restaurants: "mcc_category_restaurants",
  fast_food: "mcc_category_fast_food",
  bars: "mcc_category_bars",
  liquor: "mcc_category_liquor",
  fuel: "mcc_category_fuel",
  automotive: "mcc_category_automotive",
  apparel: "mcc_category_apparel",
  shoes: "mcc_category_shoes",
  jewelry: "mcc_category_jewelry",
  electronics: "mcc_category_electronics",
  department_stores: "mcc_category_department_stores",
  discount_stores: "mcc_category_discount_stores",
  furniture: "mcc_category_furniture",
  home_improvement: "mcc_category_home_improvement",
  books_stationery: "mcc_category_books_stationery",
  sporting_goods: "mcc_category_sporting_goods",
  toys_hobby: "mcc_category_toys_hobby",
  gifts: "mcc_category_gifts",
  florist: "mcc_category_florist",
  pets: "mcc_category_pets",
  cosmetics: "mcc_category_cosmetics",
  music_media: "mcc_category_music_media",
  general_retail: "mcc_category_general_retail",
  wholesale: "mcc_category_wholesale",
  technology: "mcc_category_technology",
  telecom: "mcc_category_telecom",
  utilities: "mcc_category_utilities",
  entertainment: "mcc_category_entertainment",
  gambling: "mcc_category_gambling",
  medical: "mcc_category_medical",
  pharmacy: "mcc_category_pharmacy",
  education: "mcc_category_education",
  financial: "mcc_category_financial",
  insurance: "mcc_category_insurance",
  real_estate: "mcc_category_real_estate",
  professional_services: "mcc_category_professional_services",
  personal_care: "mcc_category_personal_care",
  repair_services: "mcc_category_repair_services",
  cleaning_laundry: "mcc_category_cleaning_laundry",
  contractors: "mcc_category_contractors",
  shipping: "mcc_category_shipping",
  government: "mcc_category_government",
  charity: "mcc_category_charity",
  membership_orgs: "mcc_category_membership_orgs",
  services_other: "mcc_category_services_other",
};

/** Full MCC -> category table (ISO 18245). Generated; keep sorted by code. */
const MCC_TO_CATEGORY: Record<string, MccCategory> = {
  "0742": "pets",
  "0763": "services_other",
  "0780": "services_other",
  "1520": "contractors",
  "1711": "contractors",
  "1731": "contractors",
  "1740": "home_improvement",
  "1750": "contractors",
  "1761": "contractors",
  "1771": "contractors",
  "1799": "contractors",
  "2741": "professional_services",
  "2791": "professional_services",
  "2842": "cleaning_laundry",
  "3000": "airlines",
  "3001": "airlines",
  "3002": "airlines",
  "3003": "airlines",
  "3004": "airlines",
  "3005": "airlines",
  "3006": "airlines",
  "3007": "airlines",
  "3008": "airlines",
  "3009": "airlines",
  "3010": "airlines",
  "3011": "airlines",
  "3012": "airlines",
  "3013": "airlines",
  "3014": "airlines",
  "3015": "airlines",
  "3016": "airlines",
  "3017": "airlines",
  "3018": "airlines",
  "3019": "airlines",
  "3020": "airlines",
  "3021": "airlines",
  "3022": "airlines",
  "3023": "airlines",
  "3024": "airlines",
  "3025": "airlines",
  "3026": "airlines",
  "3027": "airlines",
  "3028": "airlines",
  "3029": "airlines",
  "3030": "airlines",
  "3031": "airlines",
  "3032": "airlines",
  "3033": "airlines",
  "3034": "airlines",
  "3035": "airlines",
  "3036": "airlines",
  "3037": "airlines",
  "3038": "airlines",
  "3039": "airlines",
  "3040": "airlines",
  "3041": "airlines",
  "3042": "airlines",
  "3043": "airlines",
  "3044": "airlines",
  "3045": "airlines",
  "3046": "airlines",
  "3047": "airlines",
  "3048": "airlines",
  "3049": "airlines",
  "3050": "airlines",
  "3051": "airlines",
  "3052": "airlines",
  "3053": "airlines",
  "3054": "airlines",
  "3055": "airlines",
  "3056": "airlines",
  "3057": "airlines",
  "3058": "airlines",
  "3059": "airlines",
  "3060": "airlines",
  "3061": "airlines",
  "3062": "airlines",
  "3063": "airlines",
  "3064": "airlines",
  "3065": "airlines",
  "3066": "airlines",
  "3067": "airlines",
  "3068": "airlines",
  "3069": "airlines",
  "3070": "airlines",
  "3071": "airlines",
  "3072": "airlines",
  "3073": "airlines",
  "3074": "airlines",
  "3075": "airlines",
  "3076": "airlines",
  "3077": "airlines",
  "3078": "airlines",
  "3079": "airlines",
  "3080": "airlines",
  "3081": "airlines",
  "3082": "airlines",
  "3083": "airlines",
  "3084": "airlines",
  "3085": "airlines",
  "3086": "airlines",
  "3087": "airlines",
  "3088": "airlines",
  "3089": "airlines",
  "3090": "airlines",
  "3091": "airlines",
  "3092": "airlines",
  "3093": "airlines",
  "3094": "airlines",
  "3095": "airlines",
  "3096": "airlines",
  "3097": "airlines",
  "3098": "airlines",
  "3099": "airlines",
  "3100": "airlines",
  "3101": "airlines",
  "3102": "airlines",
  "3103": "airlines",
  "3104": "airlines",
  "3105": "airlines",
  "3106": "airlines",
  "3107": "airlines",
  "3108": "airlines",
  "3109": "airlines",
  "3110": "airlines",
  "3111": "airlines",
  "3112": "airlines",
  "3113": "airlines",
  "3114": "airlines",
  "3115": "airlines",
  "3116": "airlines",
  "3117": "airlines",
  "3118": "airlines",
  "3119": "airlines",
  "3120": "airlines",
  "3121": "airlines",
  "3122": "airlines",
  "3123": "airlines",
  "3124": "airlines",
  "3125": "airlines",
  "3126": "airlines",
  "3127": "airlines",
  "3128": "airlines",
  "3129": "airlines",
  "3130": "airlines",
  "3131": "airlines",
  "3132": "airlines",
  "3133": "airlines",
  "3134": "airlines",
  "3135": "airlines",
  "3136": "airlines",
  "3137": "airlines",
  "3138": "airlines",
  "3139": "airlines",
  "3140": "airlines",
  "3141": "airlines",
  "3142": "airlines",
  "3143": "airlines",
  "3144": "airlines",
  "3145": "airlines",
  "3146": "airlines",
  "3147": "airlines",
  "3148": "airlines",
  "3149": "airlines",
  "3150": "airlines",
  "3151": "airlines",
  "3152": "airlines",
  "3153": "airlines",
  "3154": "airlines",
  "3155": "airlines",
  "3156": "airlines",
  "3157": "airlines",
  "3158": "airlines",
  "3159": "airlines",
  "3160": "airlines",
  "3161": "airlines",
  "3162": "airlines",
  "3163": "airlines",
  "3164": "airlines",
  "3165": "airlines",
  "3166": "airlines",
  "3167": "airlines",
  "3168": "airlines",
  "3169": "airlines",
  "3170": "airlines",
  "3171": "airlines",
  "3172": "airlines",
  "3173": "airlines",
  "3174": "airlines",
  "3175": "airlines",
  "3176": "airlines",
  "3177": "airlines",
  "3178": "airlines",
  "3179": "airlines",
  "3180": "airlines",
  "3181": "airlines",
  "3182": "airlines",
  "3183": "airlines",
  "3184": "airlines",
  "3185": "airlines",
  "3186": "airlines",
  "3187": "airlines",
  "3188": "airlines",
  "3189": "airlines",
  "3190": "airlines",
  "3191": "airlines",
  "3192": "airlines",
  "3193": "airlines",
  "3194": "airlines",
  "3195": "airlines",
  "3196": "airlines",
  "3197": "airlines",
  "3198": "airlines",
  "3199": "airlines",
  "3200": "airlines",
  "3201": "airlines",
  "3202": "airlines",
  "3203": "airlines",
  "3204": "airlines",
  "3205": "airlines",
  "3206": "airlines",
  "3207": "airlines",
  "3208": "airlines",
  "3209": "airlines",
  "3210": "airlines",
  "3211": "airlines",
  "3212": "airlines",
  "3213": "airlines",
  "3214": "airlines",
  "3215": "airlines",
  "3216": "airlines",
  "3217": "airlines",
  "3218": "airlines",
  "3219": "airlines",
  "3220": "airlines",
  "3221": "airlines",
  "3222": "airlines",
  "3223": "airlines",
  "3224": "airlines",
  "3225": "airlines",
  "3226": "airlines",
  "3227": "airlines",
  "3228": "airlines",
  "3229": "airlines",
  "3230": "airlines",
  "3231": "airlines",
  "3232": "airlines",
  "3233": "airlines",
  "3234": "airlines",
  "3235": "airlines",
  "3236": "airlines",
  "3237": "airlines",
  "3238": "airlines",
  "3239": "airlines",
  "3240": "airlines",
  "3241": "airlines",
  "3242": "airlines",
  "3243": "airlines",
  "3244": "airlines",
  "3245": "airlines",
  "3246": "airlines",
  "3247": "airlines",
  "3248": "airlines",
  "3249": "airlines",
  "3250": "airlines",
  "3251": "airlines",
  "3252": "airlines",
  "3253": "airlines",
  "3254": "airlines",
  "3255": "airlines",
  "3256": "airlines",
  "3257": "airlines",
  "3258": "airlines",
  "3259": "airlines",
  "3260": "airlines",
  "3261": "airlines",
  "3262": "airlines",
  "3263": "airlines",
  "3264": "airlines",
  "3265": "airlines",
  "3266": "airlines",
  "3267": "airlines",
  "3268": "airlines",
  "3269": "airlines",
  "3270": "airlines",
  "3271": "airlines",
  "3272": "airlines",
  "3273": "airlines",
  "3274": "airlines",
  "3275": "airlines",
  "3276": "airlines",
  "3277": "airlines",
  "3278": "airlines",
  "3279": "airlines",
  "3280": "airlines",
  "3281": "airlines",
  "3282": "airlines",
  "3283": "airlines",
  "3284": "airlines",
  "3285": "airlines",
  "3286": "airlines",
  "3287": "airlines",
  "3288": "airlines",
  "3289": "airlines",
  "3290": "airlines",
  "3291": "airlines",
  "3292": "airlines",
  "3293": "airlines",
  "3294": "airlines",
  "3295": "airlines",
  "3296": "airlines",
  "3297": "airlines",
  "3298": "airlines",
  "3299": "airlines",
  "3351": "car_rental",
  "3352": "car_rental",
  "3353": "car_rental",
  "3354": "car_rental",
  "3355": "car_rental",
  "3356": "car_rental",
  "3357": "car_rental",
  "3358": "car_rental",
  "3359": "car_rental",
  "3360": "car_rental",
  "3361": "car_rental",
  "3362": "car_rental",
  "3363": "car_rental",
  "3364": "car_rental",
  "3365": "car_rental",
  "3366": "car_rental",
  "3367": "car_rental",
  "3368": "car_rental",
  "3369": "car_rental",
  "3370": "car_rental",
  "3371": "car_rental",
  "3372": "car_rental",
  "3373": "car_rental",
  "3374": "car_rental",
  "3375": "car_rental",
  "3376": "car_rental",
  "3377": "car_rental",
  "3378": "car_rental",
  "3379": "car_rental",
  "3380": "car_rental",
  "3381": "car_rental",
  "3382": "car_rental",
  "3383": "car_rental",
  "3384": "car_rental",
  "3385": "car_rental",
  "3386": "car_rental",
  "3387": "car_rental",
  "3388": "car_rental",
  "3389": "car_rental",
  "3390": "car_rental",
  "3391": "car_rental",
  "3392": "car_rental",
  "3393": "car_rental",
  "3394": "car_rental",
  "3395": "car_rental",
  "3396": "car_rental",
  "3397": "car_rental",
  "3398": "car_rental",
  "3399": "car_rental",
  "3400": "car_rental",
  "3401": "car_rental",
  "3402": "car_rental",
  "3403": "car_rental",
  "3404": "car_rental",
  "3405": "car_rental",
  "3406": "car_rental",
  "3407": "car_rental",
  "3408": "car_rental",
  "3409": "car_rental",
  "3410": "car_rental",
  "3411": "car_rental",
  "3412": "car_rental",
  "3413": "car_rental",
  "3414": "car_rental",
  "3415": "car_rental",
  "3416": "car_rental",
  "3417": "car_rental",
  "3418": "car_rental",
  "3419": "car_rental",
  "3420": "car_rental",
  "3421": "car_rental",
  "3422": "car_rental",
  "3423": "car_rental",
  "3424": "car_rental",
  "3425": "car_rental",
  "3426": "car_rental",
  "3427": "car_rental",
  "3428": "car_rental",
  "3429": "car_rental",
  "3430": "car_rental",
  "3431": "car_rental",
  "3432": "car_rental",
  "3433": "car_rental",
  "3434": "car_rental",
  "3435": "car_rental",
  "3436": "car_rental",
  "3437": "car_rental",
  "3438": "car_rental",
  "3439": "car_rental",
  "3440": "car_rental",
  "3441": "car_rental",
  "3501": "lodging",
  "3502": "lodging",
  "3503": "lodging",
  "3504": "lodging",
  "3505": "lodging",
  "3506": "lodging",
  "3507": "lodging",
  "3508": "lodging",
  "3509": "lodging",
  "3510": "lodging",
  "3511": "lodging",
  "3512": "lodging",
  "3513": "lodging",
  "3514": "lodging",
  "3515": "lodging",
  "3516": "lodging",
  "3517": "lodging",
  "3518": "lodging",
  "3519": "lodging",
  "3520": "lodging",
  "3521": "lodging",
  "3522": "lodging",
  "3523": "lodging",
  "3524": "lodging",
  "3525": "lodging",
  "3526": "lodging",
  "3527": "lodging",
  "3528": "lodging",
  "3529": "lodging",
  "3530": "lodging",
  "3531": "lodging",
  "3532": "lodging",
  "3533": "lodging",
  "3534": "lodging",
  "3535": "lodging",
  "3536": "lodging",
  "3537": "lodging",
  "3538": "lodging",
  "3539": "lodging",
  "3540": "lodging",
  "3541": "lodging",
  "3542": "lodging",
  "3543": "lodging",
  "3544": "lodging",
  "3545": "lodging",
  "3546": "lodging",
  "3547": "lodging",
  "3548": "lodging",
  "3549": "lodging",
  "3550": "lodging",
  "3551": "lodging",
  "3552": "lodging",
  "3553": "lodging",
  "3554": "lodging",
  "3555": "lodging",
  "3556": "lodging",
  "3557": "lodging",
  "3558": "lodging",
  "3559": "lodging",
  "3560": "lodging",
  "3561": "lodging",
  "3562": "lodging",
  "3563": "lodging",
  "3564": "lodging",
  "3565": "lodging",
  "3566": "lodging",
  "3567": "lodging",
  "3568": "lodging",
  "3569": "lodging",
  "3570": "lodging",
  "3571": "lodging",
  "3572": "lodging",
  "3573": "lodging",
  "3574": "lodging",
  "3575": "lodging",
  "3576": "lodging",
  "3577": "lodging",
  "3578": "lodging",
  "3579": "lodging",
  "3580": "lodging",
  "3581": "lodging",
  "3582": "lodging",
  "3583": "lodging",
  "3584": "lodging",
  "3585": "lodging",
  "3586": "lodging",
  "3587": "lodging",
  "3588": "lodging",
  "3589": "lodging",
  "3590": "lodging",
  "3591": "lodging",
  "3592": "lodging",
  "3593": "lodging",
  "3594": "lodging",
  "3595": "lodging",
  "3596": "lodging",
  "3597": "lodging",
  "3598": "lodging",
  "3599": "lodging",
  "3600": "lodging",
  "3601": "lodging",
  "3602": "lodging",
  "3603": "lodging",
  "3604": "lodging",
  "3605": "lodging",
  "3606": "lodging",
  "3607": "lodging",
  "3608": "lodging",
  "3609": "lodging",
  "3610": "lodging",
  "3611": "lodging",
  "3612": "lodging",
  "3613": "lodging",
  "3614": "lodging",
  "3615": "lodging",
  "3616": "lodging",
  "3617": "lodging",
  "3618": "lodging",
  "3619": "lodging",
  "3620": "lodging",
  "3621": "lodging",
  "3622": "lodging",
  "3623": "lodging",
  "3624": "lodging",
  "3625": "lodging",
  "3626": "lodging",
  "3627": "lodging",
  "3628": "lodging",
  "3629": "lodging",
  "3630": "lodging",
  "3631": "lodging",
  "3632": "lodging",
  "3633": "lodging",
  "3634": "lodging",
  "3635": "lodging",
  "3636": "lodging",
  "3637": "lodging",
  "3638": "lodging",
  "3639": "lodging",
  "3640": "lodging",
  "3641": "lodging",
  "3642": "lodging",
  "3643": "lodging",
  "3644": "lodging",
  "3645": "lodging",
  "3646": "lodging",
  "3647": "lodging",
  "3648": "lodging",
  "3649": "lodging",
  "3650": "lodging",
  "3651": "lodging",
  "3652": "lodging",
  "3653": "lodging",
  "3654": "lodging",
  "3655": "lodging",
  "3656": "lodging",
  "3657": "lodging",
  "3658": "lodging",
  "3659": "lodging",
  "3660": "lodging",
  "3661": "lodging",
  "3662": "lodging",
  "3663": "lodging",
  "3664": "lodging",
  "3665": "lodging",
  "3666": "lodging",
  "3667": "lodging",
  "3668": "lodging",
  "3669": "lodging",
  "3670": "lodging",
  "3671": "lodging",
  "3672": "lodging",
  "3673": "lodging",
  "3674": "lodging",
  "3675": "lodging",
  "3676": "lodging",
  "3677": "lodging",
  "3678": "lodging",
  "3679": "lodging",
  "3680": "lodging",
  "3681": "lodging",
  "3682": "lodging",
  "3683": "lodging",
  "3684": "lodging",
  "3685": "lodging",
  "3686": "lodging",
  "3687": "lodging",
  "3688": "lodging",
  "3689": "lodging",
  "3690": "lodging",
  "3691": "lodging",
  "3692": "lodging",
  "3693": "lodging",
  "3694": "lodging",
  "3695": "lodging",
  "3696": "lodging",
  "3697": "lodging",
  "3698": "lodging",
  "3699": "lodging",
  "3700": "lodging",
  "3701": "lodging",
  "3702": "lodging",
  "3703": "lodging",
  "3704": "lodging",
  "3705": "lodging",
  "3706": "lodging",
  "3707": "lodging",
  "3708": "lodging",
  "3709": "lodging",
  "3710": "lodging",
  "3711": "lodging",
  "3712": "lodging",
  "3713": "lodging",
  "3714": "lodging",
  "3715": "lodging",
  "3716": "lodging",
  "3717": "lodging",
  "3718": "lodging",
  "3719": "lodging",
  "3720": "lodging",
  "3721": "lodging",
  "3722": "lodging",
  "3723": "lodging",
  "3724": "lodging",
  "3725": "lodging",
  "3726": "lodging",
  "3727": "lodging",
  "3728": "lodging",
  "3729": "lodging",
  "3730": "lodging",
  "3731": "lodging",
  "3732": "lodging",
  "3733": "lodging",
  "3734": "lodging",
  "3735": "lodging",
  "3736": "lodging",
  "3737": "lodging",
  "3738": "lodging",
  "3739": "lodging",
  "3740": "lodging",
  "3741": "lodging",
  "3742": "lodging",
  "3743": "lodging",
  "3744": "lodging",
  "3745": "lodging",
  "3746": "lodging",
  "3747": "lodging",
  "3748": "lodging",
  "3749": "lodging",
  "3750": "lodging",
  "3751": "lodging",
  "3752": "lodging",
  "3753": "lodging",
  "3754": "lodging",
  "3755": "lodging",
  "3756": "lodging",
  "3757": "lodging",
  "3758": "lodging",
  "3759": "lodging",
  "3760": "lodging",
  "3761": "lodging",
  "3762": "lodging",
  "3763": "lodging",
  "3764": "lodging",
  "3765": "lodging",
  "3766": "lodging",
  "3767": "lodging",
  "3768": "lodging",
  "3769": "lodging",
  "3770": "lodging",
  "3771": "lodging",
  "3772": "lodging",
  "3773": "lodging",
  "3774": "lodging",
  "3775": "lodging",
  "3776": "lodging",
  "3777": "lodging",
  "3778": "lodging",
  "3779": "lodging",
  "3780": "lodging",
  "3781": "lodging",
  "3782": "lodging",
  "3783": "lodging",
  "3784": "lodging",
  "3785": "lodging",
  "3786": "lodging",
  "3787": "lodging",
  "3788": "lodging",
  "3789": "lodging",
  "3790": "lodging",
  "3816": "lodging",
  "3835": "lodging",
  "4011": "public_transit",
  "4111": "public_transit",
  "4112": "public_transit",
  "4119": "medical",
  "4121": "taxi_rideshare",
  "4131": "public_transit",
  "4214": "shipping",
  "4215": "shipping",
  "4225": "shipping",
  "4411": "travel",
  "4457": "travel",
  "4468": "services_other",
  "4511": "airlines",
  "4582": "travel",
  "4722": "travel",
  "4723": "travel",
  "4784": "public_transit",
  "4789": "public_transit",
  "4812": "telecom",
  "4814": "telecom",
  "4815": "telecom",
  "4816": "technology",
  "4821": "telecom",
  "4829": "financial",
  "4899": "telecom",
  "4900": "utilities",
  "5013": "automotive",
  "5021": "furniture",
  "5039": "home_improvement",
  "5044": "professional_services",
  "5045": "technology",
  "5046": "wholesale",
  "5047": "medical",
  "5051": "home_improvement",
  "5065": "home_improvement",
  "5072": "home_improvement",
  "5074": "home_improvement",
  "5085": "wholesale",
  "5094": "jewelry",
  "5099": "wholesale",
  "5111": "books_stationery",
  "5122": "medical",
  "5131": "general_retail",
  "5137": "apparel",
  "5139": "shoes",
  "5169": "wholesale",
  "5172": "fuel",
  "5192": "books_stationery",
  "5193": "florist",
  "5198": "general_retail",
  "5199": "wholesale",
  "5200": "home_improvement",
  "5211": "home_improvement",
  "5231": "home_improvement",
  "5251": "home_improvement",
  "5261": "home_improvement",
  "5271": "general_retail",
  "5300": "wholesale",
  "5309": "general_retail",
  "5310": "discount_stores",
  "5311": "department_stores",
  "5331": "discount_stores",
  "5399": "general_retail",
  "5411": "groceries",
  "5422": "groceries",
  "5441": "groceries",
  "5451": "groceries",
  "5462": "groceries",
  "5499": "groceries",
  "5511": "automotive",
  "5521": "automotive",
  "5531": "automotive",
  "5532": "automotive",
  "5533": "automotive",
  "5541": "fuel",
  "5542": "fuel",
  "5551": "automotive",
  "5561": "utilities",
  "5571": "automotive",
  "5592": "automotive",
  "5598": "general_retail",
  "5599": "automotive",
  "5611": "apparel",
  "5621": "apparel",
  "5631": "apparel",
  "5641": "apparel",
  "5651": "apparel",
  "5655": "apparel",
  "5661": "shoes",
  "5681": "apparel",
  "5691": "apparel",
  "5697": "repair_services",
  "5698": "general_retail",
  "5699": "apparel",
  "5712": "furniture",
  "5713": "furniture",
  "5714": "furniture",
  "5718": "home_improvement",
  "5719": "furniture",
  "5722": "electronics",
  "5732": "electronics",
  "5733": "music_media",
  "5734": "technology",
  "5735": "music_media",
  "5811": "restaurants",
  "5812": "restaurants",
  "5813": "bars",
  "5814": "fast_food",
  "5815": "books_stationery",
  "5816": "technology",
  "5817": "technology",
  "5818": "technology",
  "5832": "repair_services",
  "5912": "pharmacy",
  "5921": "liquor",
  "5931": "general_retail",
  "5932": "general_retail",
  "5933": "automotive",
  "5935": "automotive",
  "5937": "general_retail",
  "5940": "sporting_goods",
  "5941": "sporting_goods",
  "5942": "books_stationery",
  "5943": "books_stationery",
  "5944": "jewelry",
  "5945": "toys_hobby",
  "5946": "professional_services",
  "5947": "gifts",
  "5948": "general_retail",
  "5949": "general_retail",
  "5950": "furniture",
  "5960": "insurance",
  "5961": "books_stationery",
  "5962": "services_other",
  "5963": "general_retail",
  "5964": "general_retail",
  "5965": "general_retail",
  "5966": "general_retail",
  "5967": "services_other",
  "5968": "general_retail",
  "5969": "general_retail",
  "5970": "toys_hobby",
  "5971": "general_retail",
  "5972": "general_retail",
  "5973": "charity",
  "5975": "medical",
  "5976": "medical",
  "5977": "cosmetics",
  "5978": "services_other",
  "5983": "fuel",
  "5992": "florist",
  "5993": "general_retail",
  "5994": "books_stationery",
  "5995": "pets",
  "5996": "entertainment",
  "5997": "utilities",
  "5998": "general_retail",
  "5999": "general_retail",
  "6010": "financial",
  "6011": "financial",
  "6012": "financial",
  "6051": "financial",
  "6211": "financial",
  "6300": "insurance",
  "6381": "insurance",
  "6399": "insurance",
  "6513": "real_estate",
  "7011": "lodging",
  "7012": "travel",
  "7032": "entertainment",
  "7033": "lodging",
  "7210": "cleaning_laundry",
  "7211": "cleaning_laundry",
  "7216": "cleaning_laundry",
  "7217": "furniture",
  "7221": "professional_services",
  "7230": "personal_care",
  "7251": "shoes",
  "7261": "professional_services",
  "7273": "services_other",
  "7276": "services_other",
  "7277": "services_other",
  "7278": "services_other",
  "7296": "apparel",
  "7297": "personal_care",
  "7298": "personal_care",
  "7299": "services_other",
  "7311": "professional_services",
  "7321": "financial",
  "7332": "services_other",
  "7333": "professional_services",
  "7338": "professional_services",
  "7339": "professional_services",
  "7342": "services_other",
  "7349": "cleaning_laundry",
  "7361": "professional_services",
  "7372": "technology",
  "7375": "technology",
  "7379": "technology",
  "7392": "professional_services",
  "7393": "services_other",
  "7394": "electronics",
  "7395": "medical",
  "7399": "services_other",
  "7511": "fuel",
  "7512": "car_rental",
  "7513": "utilities",
  "7519": "car_rental",
  "7523": "public_transit",
  "7531": "automotive",
  "7534": "automotive",
  "7535": "automotive",
  "7538": "automotive",
  "7542": "automotive",
  "7549": "automotive",
  "7622": "electronics",
  "7623": "repair_services",
  "7629": "electronics",
  "7631": "jewelry",
  "7641": "furniture",
  "7692": "repair_services",
  "7699": "repair_services",
  "7800": "gambling",
  "7801": "gambling",
  "7802": "government",
  "7829": "music_media",
  "7832": "entertainment",
  "7841": "music_media",
  "7911": "entertainment",
  "7922": "entertainment",
  "7929": "entertainment",
  "7932": "entertainment",
  "7933": "entertainment",
  "7941": "sporting_goods",
  "7991": "travel",
  "7992": "entertainment",
  "7993": "music_media",
  "7994": "music_media",
  "7995": "gambling",
  "7996": "entertainment",
  "7997": "entertainment",
  "7998": "entertainment",
  "7999": "entertainment",
  "8011": "medical",
  "8021": "medical",
  "8031": "medical",
  "8041": "medical",
  "8042": "medical",
  "8043": "medical",
  "8044": "medical",
  "8049": "medical",
  "8050": "medical",
  "8062": "medical",
  "8071": "medical",
  "8099": "medical",
  "8111": "professional_services",
  "8211": "education",
  "8220": "education",
  "8241": "education",
  "8244": "education",
  "8249": "education",
  "8299": "education",
  "8351": "education",
  "8398": "charity",
  "8641": "charity",
  "8651": "charity",
  "8661": "charity",
  "8675": "automotive",
  "8699": "membership_orgs",
  "8734": "medical",
  "8911": "professional_services",
  "8931": "books_stationery",
  "8999": "professional_services",
  "9211": "government",
  "9222": "government",
  "9223": "government",
  "9311": "government",
  "9399": "government",
  "9402": "government",
  "9405": "government",
  "9700": "services_other",
  "9701": "services_other",
  "9702": "services_other",
  "9950": "financial",
};

/** Returns the i18n key for an MCC's category, or `null` when unmapped. */
export const getMccCategoryTKey = (code: string | null | undefined): string | null => {
  if (!code) return null;
  const category = MCC_TO_CATEGORY[code.trim()];
  return category ? MCC_CATEGORY_TKEY[category] : null;
};
